'use client';

import { Fragment, useEffect } from 'react';

import dayjs from 'dayjs';

import EventCardMobile from '@/components/event-cards/event-card.mobile';
import { EventCardDate } from '@/components/event-cards/event-card-date';
import { useTranslation } from '@/i18n/client';
import { EventCardT } from '@/types/calendar';
import { WithLang } from '@/types/general';
import { storageKeys } from '@/utils/constants';

interface Props {
    events: EventCardT[];
    isLoading: boolean;
    notFoundContext: 'day' | 'range' | null;
    withDates: boolean;
}

const compareDate = (eventDate: string, date: string) => dayjs(eventDate).startOf('day').isSame(dayjs(date));

export default function EventCardsMobile(props: WithLang<Props>) {
    const dates = Array.from(
        new Set<string>(props.events.map((event) => dayjs(event.start_date).startOf('day').toISOString())),
    );
    const { t } = useTranslation(props.lang, 'calendar');

    useEffect(() => {
        if (!props.isLoading) {
            const storedScroll = sessionStorage.getItem(storageKeys.scrollPosition);
            if (storedScroll) {
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        window.scrollTo({ top: +storedScroll });
                        sessionStorage.removeItem(storageKeys.scrollPosition);
                    }, 10);
                });
            }
        }
    }, [props.isLoading]);

    return (
        <section className="mx-auto sm:w-3/4">
            {props.isLoading && (
                <div>
                    <p className="my-12 h-8 w-80 animate-pulse bg-gradient-to-tr from-gray-200 to-gray-300"></p>
                    <div className="cards-mobile">
                        {Array.from({ length: 3 }).map((_, index) => (
                            <div key={index} className="my-6 flex h-[332px] animate-pulse flex-col bg-gray-100">
                                <div className="h-[192px] animate-pulse bg-gradient-to-tr from-gray-200 to-gray-300" />

                                <div className="mx-auto mb-4 mt-auto w-[90%]">
                                    <p className="mb-2 mt-auto h-5 w-full animate-pulse bg-gradient-to-tr from-gray-200 to-gray-300"></p>
                                    <p className="h-5 w-full animate-pulse bg-gradient-to-tr from-gray-200 to-gray-300"></p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {props.withDates ? (
                dates.length > 0 &&
                dates.map((date) => (
                    <Fragment key={date}>
                        <EventCardDate dayString={date} lang={props.lang} />
                        <div className="cards-mobile mb-24 last-of-type:mb-8">
                            {props.events.map(
                                (event) =>
                                    compareDate(event.start_date, date) && (
                                        <EventCardMobile key={event.id} event={event} lang={props.lang} />
                                    ),
                            )}
                        </div>
                    </Fragment>
                ))
            ) : (
                <>
                    <div className="cards-mobile sm:cards-desktop mb-24 last-of-type:mb-8">
                        {props.events.map((event) => (
                            <EventCardMobile key={event.id} event={event} lang={props.lang} />
                        ))}
                    </div>
                </>
            )}
            {props.events.length === 0 && !props.isLoading && (
                <div className="mx-auto my-auto h-full self-center text-center">
                    <p className="my-32 text-3xl">{t('messages.noEvents', { context: props.notFoundContext })}</p>
                </div>
            )}
        </section>
    );
}
